// THE RULES AROUND THE SETTLE-LATER GUARD on Book trade, as pure functions — so the screen and its tests read ONE statement of each.
//
// Three steps stand between a settle-later form and a posting, and a review round found a way round each: who the party IS (the typed
// text, normalized the way the books normalize it), whether the switch may be cleared under the operator, and whether the trade may go
// at all (the conversion gate). All three stand on reads — `ReadState`, lib/read-state.ts — and none of them takes "has not answered"
// for "nothing there".

import type { ReadState } from './read-state';

/** THE PARTY'S IDENTITY AS THE BOOKS READ IT. The BFF trims and lowercases `party_slug` before it looks the party up
 *  (bff/src/routes/trades.ts), so "hartley-trading ", "HARTLEY-TRADING" and "hartley-trading" are ONE party and book to the same
 *  account. Every comparison, query key and read on the screen goes through this — never the raw text of the box, which keeps what was
 *  typed. (A guard keyed to the raw text lets a trailing space or a capital letter walk round it.) */
export const partyKeyOf = (typed: string): string => typed.trim().toLowerCase();

/** MAY THE SCREEN CLEAR THE SETTLE-LATER SWITCH under the operator? Only on a KNOWN ineligibility: the party lookup and the box both
 *  SUCCEEDED, the party exists, and together they say this customer cannot settle later (not a merchant, no open position). While
 *  either read is waiting or failed the switch stays as the operator — or a ?later=1 link — set it: clearing it there turns the trade
 *  into a settled one, restores the treasury rails, and walks round the guard that is waiting for the very box that would decide. */
export function shouldClearSettleLater(a: { partyExists: boolean; lookup: ReadState; box: ReadState; canSettleLater: boolean }): boolean {
  return a.lookup === 'known' && a.partyExists && a.box === 'known' && !a.canSettleLater;
}

export type ConversionGate = 'ask' | 'unchecked' | null;

/** THE CONVERSION CHECK for a trade of the given shape: nothing in the way (null) — or the box already holds what they would pay and
 *  nobody has answered ('ask') — or something it stands on has not been SUCCESSFULLY read ('unchecked': a check keyed to a figure
 *  nobody has is not a check passed, so the trade does not go out; quieting the "you already hold" warning must never be what opens
 *  the button). Two reads stand under it, in this order: WHO the party is (the lookup — a pending or failed lookup makes an existing
 *  party look like a new name, so it is not "nobody to check", it is unresolved) and then WHAT THEY HOLD (the box).
 *  `answered`: this very press is the answer to the question; `acknowledged`: it was answered for this party, asset, amount and shape. */
export function conversionGateOf(a: {
  later: boolean;
  partyKey: string;
  takeN: number;
  lookup: ReadState;
  partyExists: boolean;
  box: ReadState;
  heldTake: number;
  dust: number;
  answered: boolean;
  acknowledged: boolean;
}): ConversionGate {
  if (!a.later || !a.partyKey || !(a.takeN > 0)) return null;
  if (a.lookup !== 'known') return 'unchecked';
  if (!a.partyExists) return null;   // the lookup SUCCEEDED and found nobody by that name: a new party — no box to read, and the books ask
  if (a.box !== 'known') return 'unchecked';
  return a.heldTake > a.dust && a.heldTake + a.dust >= a.takeN && !a.answered && !a.acknowledged ? 'ask' : null;
}
